import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import Logo from "components/ui/Logo";
import BrandingFooter from "components/ui/BrandingFooter";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import NotFound from "pages/NotFound";
import StaffDashboard from './pages/staff-dashboard';
import AnalyticsDashboard from './pages/analytics-dashboard';
import MemberRegistration from './pages/member-registration';
import TrainerDashboard from './pages/trainer-dashboard';
import Login from './pages/login';
import GymOwnerSignup from './pages/gym-owner-signup';
import SubscriptionPayment from './pages/gym-owner-signup/subscription-payment';
import MemberManagement from './pages/member-management';
import WorkoutPrograms from './pages/workout-programs';
import ClassBooking from './pages/class-booking';
import MemberDashboard from './pages/member-dashboard';
import Messaging from './pages/messaging';
import SingleGymOwnerDashboard from './pages/single-gym-owner-dashboard';
import EquipmentManagement from './pages/equipment-management';
import EquipmentLifecycleManagement from './pages/equipment-lifecycle-management';
import MemberRetention from './pages/member-retention';
import LiveOperationsFeed from './pages/live-operations-feed';
import StaffMessagingHub from './pages/staff-messaging-hub';
import AutomatedReportGeneration from './pages/automated-report-generation';
import POSInventoryManagement from './pages/pos-inventory-management';
import StaffPerformanceAnalytics from './pages/staff-performance-analytics';
import FinancialReconciliationDashboard from './pages/financial-reconciliation-dashboard';
import SystemAdministratorDashboard from './pages/system-administrator-dashboard';
import LocationBasedPOSInventory from './pages/location-based-pos-inventory';
import AIHelpAssistant from './pages/ai-help-assistant';
import MemberCommunicationHub from './pages/member-communication-hub';
import MobileCheckInHub from './pages/mobile-check-in-hub';
import StaffAttendanceTracking from './pages/staff-attendance-tracking';
import PayrollHRManagementSystem from './pages/payroll-hr-management-system';
import CriticalAlertManagementCenter from './pages/critical-alert-management-center';
import AutomatedMaintenanceScheduling from './pages/automated-maintenance-scheduling';
import WorkOrderManagementSystem from './pages/work-order-management-system';
import MobileMaintenanceOperations from './pages/mobile-maintenance-operations';
import SubscriptionManagementCenter from './pages/subscription-management-center';
import PaymentRecoveryRetentionCenter from './pages/payment-recovery-retention-center';
import MemberGamificationHub from './pages/member-gamification-hub';
import AchievementRewardsManagement from './pages/achievement-rewards-management';
import SocialChallengesLeaderboards from './pages/social-challenges-leaderboards';
import TermsAndConditions from './pages/terms-and-conditions';
import PrivacyPolicy from './pages/privacy-policy';
import SystemAdministratorLogin from './pages/system-administrator-login';
import SystemAuditTrailDashboard from './pages/system-audit-trail-dashboard';
import ComplianceMonitoringCenter from './pages/compliance-monitoring-center';
import SecurityEventInvestigationPortal from './pages/security-event-investigation-portal';
import GymManagementCenter from './pages/gym-management-center';
import UserProfileSettings from './pages/user-profile-settings';
import TwoFactorAuthenticationSetup from './pages/two-factor-authentication-setup';
import SessionManagementCenter from './pages/session-management-center';
import StaffQRCodePINLogin from './pages/staff-qr-code-pin-login';

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <ScrollToTop />
          <Logo />
          <RouterRoutes>
            {/* Public routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/system-administrator-login" element={<SystemAdministratorLogin />} />
            <Route path="/gym-owner-signup" element={<GymOwnerSignup />} />
            <Route path="/gym-owner-signup/subscription-payment" element={<SubscriptionPayment />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            
            {/* Protected routes */}
            <Route path="/staff-dashboard" element={<ProtectedRoute><StaffDashboard /></ProtectedRoute>} />
            <Route path="/analytics-dashboard" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
            <Route path="/member-registration" element={<ProtectedRoute><MemberRegistration /></ProtectedRoute>} />
            <Route path="/trainer-dashboard" element={<ProtectedRoute><TrainerDashboard /></ProtectedRoute>} />
            <Route path="/member-management" element={<ProtectedRoute><MemberManagement /></ProtectedRoute>} />
            <Route path="/single-gym-owner-dashboard" element={<ProtectedRoute><SingleGymOwnerDashboard /></ProtectedRoute>} />
            <Route path="/workout-programs" element={<ProtectedRoute><WorkoutPrograms /></ProtectedRoute>} />
            <Route path="/class-booking" element={<ProtectedRoute><ClassBooking /></ProtectedRoute>} />
            <Route path="/member-dashboard" element={<ProtectedRoute><MemberDashboard /></ProtectedRoute>} />
            <Route path="/messaging" element={<ProtectedRoute><Messaging /></ProtectedRoute>} />
            <Route path="/equipment-management" element={<ProtectedRoute><EquipmentManagement /></ProtectedRoute>} />
            <Route path="/equipment-lifecycle-management" element={<EquipmentLifecycleManagement />} />
            <Route path="/member-communication-hub" element={<MemberCommunicationHub />} />
            <Route path="/member-retention" element={<ProtectedRoute><MemberRetention /></ProtectedRoute>} />
            <Route path="/live-operations-feed" element={<ProtectedRoute><LiveOperationsFeed /></ProtectedRoute>} />
            <Route path="/staff-messaging-hub" element={<ProtectedRoute><StaffMessagingHub /></ProtectedRoute>} />
            <Route path="/automated-report-generation" element={<ProtectedRoute><AutomatedReportGeneration /></ProtectedRoute>} />
            <Route path="/pos-inventory-management" element={<ProtectedRoute><POSInventoryManagement /></ProtectedRoute>} />
            <Route path="/staff-performance-analytics" element={<ProtectedRoute><StaffPerformanceAnalytics /></ProtectedRoute>} />
            <Route path="/financial-reconciliation-dashboard" element={<ProtectedRoute><FinancialReconciliationDashboard /></ProtectedRoute>} />
            <Route path="/system-administrator-dashboard" element={<ProtectedRoute><SystemAdministratorDashboard /></ProtectedRoute>} />
            <Route path="/location-based-pos-inventory" element={<ProtectedRoute><LocationBasedPOSInventory /></ProtectedRoute>} />
            <Route path="/ai-help-assistant" element={<ProtectedRoute><AIHelpAssistant /></ProtectedRoute>} />
            <Route path="/mobile-check-in-hub" element={<ProtectedRoute><MobileCheckInHub /></ProtectedRoute>} />
            <Route path="/staff-attendance-tracking" element={<ProtectedRoute><StaffAttendanceTracking /></ProtectedRoute>} />
            <Route path="/payroll-hr-management-system" element={<ProtectedRoute><PayrollHRManagementSystem /></ProtectedRoute>} />
            <Route path="/critical-alert-management-center" element={<ProtectedRoute><CriticalAlertManagementCenter /></ProtectedRoute>} />
            <Route path="/automated-maintenance-scheduling" element={<ProtectedRoute><AutomatedMaintenanceScheduling /></ProtectedRoute>} />
            <Route path="/work-order-management-system" element={<ProtectedRoute><WorkOrderManagementSystem /></ProtectedRoute>} />
            <Route path="/mobile-maintenance-operations" element={<ProtectedRoute><MobileMaintenanceOperations /></ProtectedRoute>} />
            <Route path="/subscription-management-center" element={<ProtectedRoute><SubscriptionManagementCenter /></ProtectedRoute>} />
            <Route path="/payment-recovery-retention-center" element={<ProtectedRoute><PaymentRecoveryRetentionCenter /></ProtectedRoute>} />
            <Route path="/member-gamification-hub" element={<ProtectedRoute><MemberGamificationHub /></ProtectedRoute>} />
            <Route path="/achievement-rewards-management" element={<ProtectedRoute><AchievementRewardsManagement /></ProtectedRoute>} />
            <Route path="/social-challenges-leaderboards" element={<ProtectedRoute><SocialChallengesLeaderboards /></ProtectedRoute>} />
            <Route path="/system-audit-trail-dashboard" element={<ProtectedRoute><SystemAuditTrailDashboard /></ProtectedRoute>} />
            <Route path="/compliance-monitoring-center" element={<ProtectedRoute><ComplianceMonitoringCenter /></ProtectedRoute>} />
            <Route path="/security-event-investigation-portal" element={<ProtectedRoute><SecurityEventInvestigationPortal /></ProtectedRoute>} />
            <Route path="/gym-management-center" element={<ProtectedRoute><GymManagementCenter /></ProtectedRoute>} />
            <Route path="/user-profile-settings" element={<ProtectedRoute><UserProfileSettings /></ProtectedRoute>} />
            <Route path="/two-factor-authentication-setup" element={<ProtectedRoute><TwoFactorAuthenticationSetup /></ProtectedRoute>} />
            <Route path="/session-management-center" element={<ProtectedRoute><SessionManagementCenter /></ProtectedRoute>} />
            <Route path="/staff-qr-code-pin-login" element={<StaffQRCodePINLogin />} />
            
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
          <BrandingFooter />
          <AIHelpAssistant />
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
